import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { focusNextEnterNav } from '../../lib/focusEnterNav';
import { CalendarIcon, XIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year: number, month: number): Date[] {
  const last = new Date(year, month + 1, 0);
  const days: Date[] = [];
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  return days;
}

function getCalendarGrid(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const days = getDaysInMonth(year, month);
  const grid: (Date | null)[] = [];
  for (let i = 0; i < startWeekday; i++) grid.push(null);
  days.forEach((d) => grid.push(d));
  while (grid.length < 42) grid.push(null);
  return grid;
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isToday(d: Date | null): boolean {
  if (!d) return false;
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

/** Normalize value to YYYY-MM-DD. Accepts ISO string or YYYY-MM-DD. Parses date-only as local to avoid UTC-offset showing previous day. */
function toDateOnly(value: string): string {
  if (!value || !value.trim()) return '';
  const s = value.trim();
  let d: Date;
  const dateOnlyMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, y, m, day] = dateOnlyMatch.map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(s);
  }
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value?: string;
  onChange?: (event: { target: { name?: string; value: string; type: string } }) => void;
  placeholder?: string;
  showClearButton?: boolean;
  showQuickFill?: boolean;
  showFormatHint?: boolean;
  minDate?: string;
  maxDate?: string;
  dateFormat?: string;
  error?: string;
  helperText?: string;
  /** When true, Enter moves focus to the next field marked with data-enter-nav. */
  enterNav?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  name,
  placeholder = 'Select date',
  required = false,
  readOnly = false,
  disabled = false,
  className,
  minDate,
  maxDate,
  showClearButton = true,
  showQuickFill = true,
  showFormatHint = true,
  dateFormat = 'yyyy-MM-dd',
  error,
  helperText,
  enterNav = false,
  ...props
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [keyboardInputValue, setKeyboardInputValue] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      setDropdownRect(null);
      return;
    }
    const updateRect = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownRect({
          top: rect.bottom + 6,
          left: rect.left,
          width: Math.min(320, Math.max(rect.width, 280)),
        });
      }
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [isOpen]);

  const valueNorm = value ? toDateOnly(value) : '';
  const valueDate = valueNorm ? new Date(valueNorm + 'T12:00:00') : null;
  const isValidValue = valueDate && !isNaN(valueDate.getTime());

  const [viewMonth, setViewMonth] = useState(() => {
    if (isValidValue) return new Date(valueDate!.getFullYear(), valueDate!.getMonth(), 1);
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    isValidValue ? new Date(valueDate!.getFullYear(), valueDate!.getMonth(), valueDate!.getDate()) : null
  );

  const minDateObj = minDate ? new Date(minDate) : null;
  const maxDateObj = maxDate ? new Date(maxDate) : null;

  const formatDateForDisplay = (dateString: string): string => {
    const norm = toDateOnly(dateString);
    if (!norm) return '';
    const [y, m, d] = norm.split('-');
    return `${d}/${m}/${y}`;
  };

  const emitValue = (date: Date | null) => {
    if (!date) {
      onChange?.({ target: { name, value: '', type: 'date' } });
      return;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    onChange?.({ target: { name, value: `${y}-${m}-${d}`, type: 'date' } });
  };

  const parseTypedDate = (raw: string): Date | null => {
    const s = raw.trim();
    if (!s) return null;
    const match = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (match) {
      const [, day, month, year] = match;
      const d = parseInt(day, 10);
      const mo = parseInt(month, 10) - 1;
      const y = year.length <= 2 ? 2000 + parseInt(year, 10) : parseInt(year, 10);
      const parsed = new Date(y, mo, d);
      if (!isNaN(parsed.getTime()) && parsed.getDate() === d && parsed.getMonth() === mo) return parsed;
    }
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;
    return null;
  };

  const commitKeyboardInput = () => {
    const parsed = parseTypedDate(keyboardInputValue);
    if (parsed) {
      setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
      setSelectedDate(new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
      emitValue(new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
    }
    setKeyboardInputValue('');
  };

  useEffect(() => {
    if (valueNorm) {
      const d = new Date(valueNorm + 'T12:00:00');
      if (!isNaN(d.getTime())) {
        setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
      }
    } else {
      setSelectedDate(null);
    }
  }, [valueNorm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setKeyboardInputValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectDay = (d: Date | null) => {
    if (!d) return;
    if (minDateObj && d < minDateObj) return;
    if (maxDateObj && d > maxDateObj) return;
    setSelectedDate(d);
    emitValue(d);
    if (enterNav) {
      setIsOpen(false);
      setKeyboardInputValue('');
      requestAnimationFrame(() => focusNextEnterNav(containerRef.current));
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSelectedDate(null);
    setKeyboardInputValue('');
    onChange?.({ target: { name, value: '', type: 'date' } });
  };

  const quickFillToday = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    const d = new Date();
    setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    emitValue(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const isDayDisabled = (d: Date | null) => {
    if (!d) return true;
    if (minDateObj && d < minDateObj) return true;
    if (maxDateObj && d > maxDateObj) return true;
    return false;
  };

  const hasValue = !!valueNorm;
  const grid = getCalendarGrid(viewMonth.getFullYear(), viewMonth.getMonth());
  const currentDateDisplay = valueNorm
    ? formatDateForDisplay(valueNorm)
    : selectedDate
      ? `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`
      : '';

  return (
    <div ref={containerRef} className="relative" data-enter-nav={enterNav ? '' : undefined}>
      <input type="hidden" name={name} value={valueNorm || ''} readOnly {...props} />

      <div className="relative">
        <div
          className={cn(
            'flex items-center w-full min-w-0 gap-1 rounded-lg transition-all duration-200',
            'px-2 py-2 text-sm border',
            colors.surface,
            colors.border,
            isOpen && 'ring-2 shadow-sm',
            isOpen && colors.primaryBorder,
            !isOpen && !error && colors.interactiveHover,
            error && 'border-red-500',
            disabled && 'opacity-50 cursor-not-allowed',
            disabled && colors.surfaceHover,
            readOnly && 'cursor-default',
            readOnly && colors.surfaceHover,
            !disabled && !readOnly && 'cursor-pointer',
            className
          )}
          onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isOpen && enterNav) {
              e.preventDefault();
              focusNextEnterNav(containerRef.current);
            }
          }}
          tabIndex={disabled || readOnly ? -1 : 0}
          data-enter-focus={enterNav ? '' : undefined}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <CalendarIcon
            className={cn('h-3.5 w-3.5 shrink-0', hasValue ? colors.primaryFg : colors.textMuted, !hasValue && 'opacity-70')}
          />
          <div className={cn('min-w-0 flex-1 overflow-hidden', !hasValue && 'opacity-70')}>
            {hasValue ? (
              <span className={cn('block truncate font-medium tabular-nums text-sm', colors.primaryFg)}>
                {formatDateForDisplay(valueNorm)}
              </span>
            ) : (
              <span className={cn('block truncate text-sm', colors.textMuted)}>{placeholder || 'Date'}</span>
            )}
          </div>
          {showClearButton && hasValue && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'ml-auto shrink-0 rounded p-1 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors',
                colors.textMuted,
                colors.interactiveHover,
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
              )}
              title="Clear"
              tabIndex={-1}
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {isOpen && !readOnly && !disabled && dropdownRect &&
        createPortal(
          <div
            ref={dropdownRef}
            className={cn(
              'fixed rounded-xl shadow-lg border overflow-hidden z-[100]',
              colors.surface,
              colors.border
            )}
            style={{
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
            }}
          >
          <div className={cn('flex flex-row gap-2 px-2.5 py-2 items-center border-b', colors.border, colors.text, colors.surfaceHover)}>
            <span className={cn('text-xs font-semibold uppercase tracking-wider shrink-0', colors.textMuted)}>Date</span>
            <div
              className={cn(
                'flex-1 min-w-0 px-2 py-1 rounded border text-sm font-medium tabular-nums',
                colors.border,
                colors.surface,
                colors.text
              )}
            >
              {selectedDate
                ? `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`
                : ''}
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); setKeyboardInputValue(''); }}
              className={cn('p-1.5 rounded-md flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-0', colors.textMuted, colors.interactiveHover)}
              aria-label="Close"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="px-2.5 py-2">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between gap-1 mb-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
                  }}
                  className={cn('p-1 rounded border transition-colors', colors.border, colors.interactiveHover, colors.text)}
                  aria-label="Previous month"
                >
                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                </button>
                <span className={cn('text-sm font-semibold flex-1 text-center tabular-nums', colors.text)}>
                  {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
                  }}
                  className={cn('p-1 rounded border transition-colors', colors.border, colors.interactiveHover, colors.text)}
                  aria-label="Next month"
                >
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {WEEKDAYS.map((wd) => (
                  <div key={wd} className={cn('text-center text-xs font-semibold py-0.5', colors.textMuted)}>
                    {wd}
                  </div>
                ))}
                {grid.map((cell, i) => {
                  const disabled = isDayDisabled(cell);
                  const selected = selectedDate && cell && isSameDay(cell, selectedDate);
                  const today = isToday(cell);
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectDay(cell);
                      }}
                      className={cn(
                        'w-7 h-7 rounded text-sm font-medium transition-all duration-150',
                        !cell && 'invisible',
                        cell && disabled && 'opacity-40 cursor-not-allowed',
                        cell && !disabled && !selected && colors.interactiveHover,
                        selected && cn(colors.primary, colors.primaryText, 'shadow-sm hover:opacity-90'),
                        !selected && today && 'ring-2',
                        !selected && today && colors.primaryBorder,
                        !selected && today && colors.surfaceHover,
                        cell && !selected && !disabled && colors.text
                      )}
                    >
                      {cell ? cell.getDate() : ''}
                    </button>
                  );
                })}
              </div>
              <div className={cn('pt-1 mt-0.5 border-t', colors.border)}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClear(e); }}
                  className={cn('text-xs font-medium px-1.5 py-0.5 rounded transition-colors', colors.primaryFg, colors.interactiveHover)}
                >
                  Clear
                </button>
              </div>
            </div>

            <div
              className={cn(
                'mt-2 rounded-lg border px-2.5 py-2',
                colors.border,
                colors.surfaceHover
              )}
            >
              <div className={cn('flex items-center gap-1.5 mb-1.5', colors.text)}>
                <span className={cn('text-xs font-medium whitespace-nowrap', colors.textMuted)}>Or type</span>
                <input
                  type="text"
                  value={keyboardInputValue !== '' ? keyboardInputValue : currentDateDisplay}
                  onChange={(e) => setKeyboardInputValue(e.target.value)}
                  onFocus={() => setKeyboardInputValue(currentDateDisplay)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitKeyboardInput();
                      if (enterNav) {
                        setIsOpen(false);
                        requestAnimationFrame(() => focusNextEnterNav(containerRef.current));
                      }
                    }
                  }}
                  onBlur={commitKeyboardInput}
                  placeholder="DD/MM/YYYY"
                  className={cn(
                    'flex-1 min-w-0 px-2 py-1.5 text-sm border rounded outline-none focus:ring-2 focus:ring-offset-0 transition-shadow',
            colors.border,
            colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
                    colors.surface,
                    colors.border,
                    colors.text
                  )}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {showQuickFill && (
                <div className="flex flex-wrap items-center gap-1">
                  <span className={cn('text-xs font-medium', colors.textMuted)}>Quick</span>
                  <button
                    type="button"
                    onClick={quickFillToday}
                    className={cn(
                      'px-2 py-1 text-xs font-medium rounded transition-colors',
                      colors.info,
                      colors.interactiveHover
                    )}
                    title="Today's date"
                  >
                    Today
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
          document.body
        )}

      <div className="mt-1 flex flex-col gap-0.5">
        {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
        {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
      </div>
    </div>
  );
};

DateInput.displayName = 'DateInput';

export default DateInput;
